<template>
  <div id="partsStoreOrders">
    <div v-if="!userIsOrderAdmin" class="container">
      <b-alert show variant="danger">
        {{ getContentItem("niceNoAccessToOrder") }}
      </b-alert>
    </div>
    <div v-else class="container">
      <b-row>
        <b-col xs="12" lg="3">
          <OrdersMenu />
          <br />
        </b-col>
        <b-col xs="12" lg="9">
          <h1>{{ getContentItem("partsStoreOrders") }}</h1>

          <div class="order-filters">
            <b-row>
              <b-col xs="12" lg="4">
                <MonthSelector number-of-months="13" @selectMonth="SetUserSelectedMonth" />
              </b-col>
            </b-row>
            <b-row>
              <b-col xs="12" lg="10">
                <AvailableDealerCodeSelector
                  :show-all-branches-option="true"
                  :default-dealercode="selectedOption"
                  @selectDealerCode="SetUserDealerCode"
                />
              </b-col>
              <b-col xs="12" lg="2">
                <b-button id="viewButton" class="btn btn-view" @click="FilterButtonClick">
                  {{ getContentItem("viewButton") }}
                </b-button>
              </b-col>
            </b-row>
          </div>
          <br />
          <b-row>
            <b-col xs="12">
              <b-alert id="orderMessage" show variant="secondary">
                {{ message }}
              </b-alert>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group :label-cols="3" :label="getContentItem('perPage')">
                <b-form-select @change="OrderHeaderSearch" v-model="pagination.perPage" size="sm" :options="pagination.perPageList" class="mt-2" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group>
                <b-form-input
                  v-model="searchQuery"
                  size="sm"
                  :placeholder="getContentItem('search')"
                  class="mt-2"
                  @keyup.native="SearchBoxFilter"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col xs="12">
              <DnTable
                id="partsStoreOrdersTable"
                ref="partsStoreOrdersTableRef"
                class="blue"
                :fields="partsStoreOrderColumns"
                :items="psOrders"
                :sortBy="orderByField"
                :sortDescProp="sortDesc"
                :perPage="pagination.perPage"
                :busy="isLoading"
                :showPagination="true"
                :usePagination="false"
                :emptyText="'No Orders Found'"
                :totalRows="pagination.totalRows"
                @sort-changed="paginationReset"
                @update:currentPage="handlePageChange"
              >
                <template #OrderNumber="{ row }">
                  <router-link :to="'/partsStore/Orders/Details?orderId=' + row.item.OrderId">
                    {{ row.item.OrderNumber }}
                  </router-link>
                </template>
                <template #Customer="{ row }">
                  <span> {{ row.item.CustomerName }} ({{ row.item.CustomerNumber }}) </span>
                </template>
                <template #FulfillmentAccount="{ row }">
                  <span> {{ row.item.FulfillmentAccountName }} ({{ row.item.FulfillmentAccountCode }}) </span>
                </template>
                <template #CreatedDate="{ row }">
                  {{ getLocalDateTime(row.item.CreatedDate, false, true) }}
                </template>
              </DnTable>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import OrdersMenu from "@/components/SubMenus/OrdersMenu";
import MonthSelector from "@/components/Dropdowns/MonthSelector";
import AvailableDealerCodeSelector from "@/components/Dropdowns/AvailableDealerCodeSelector";
import { PartsStoreOrdersService } from "@/services/PartsStoreOrdersService";
import { ErrorLogService } from "@/services/ErrorLogService";
import { sessionHelper } from "@/components/mixins/sessionHelper";
import { stringHelper } from "@/components/mixins/stringHelper";
import { formatDateTime } from "@/components/mixins/formatDateTime";
import { paginationMixin } from "@/components/mixins/paginationMixin";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default {
  name: "partsStoreOrders",
  components: {
    OrdersMenu,
    MonthSelector,
    AvailableDealerCodeSelector,
    DnTable,
  },
  mixins: [stringHelper, formatDateTime, sessionHelper, paginationMixin],
  data: () => ({
    partsStoreOrders: [],
    partsStoreOrderColumns: [
      { key: "OrderNumber", label: "", sortable: true },
      { key: "Customer", label: "", sortable: true },
      { key: "PoNumber", label: "", sortable: true },
      { key: "FulfillmentAccount", label: "", sortable: true },
      { key: "CreatedDate", label: "", sortable: true },
      { key: "Status", label: "", sortable: true },
      { key: "Total", label: "", sortable: true },
    ],
    orderByField: "Id",
    useUserFilter: false,
    sortDesc: true,
    searchQuery: "",
    timer: 0,
    orderStatusFilter: [1, 2, 6, 7], //Submitted, Processed, Pending Approval, Approved
    monthFilter: "",
    dealerFilter: [],
    selectedOption: "All Branches",
    message: "You are viewing all open orders for All Branches",
    allAvailableDealerCodes: [],
    psOrders: [],
    isLoading: true,
  }),
  computed: {
    ...mapGetters(["user", "getContentItem", "hasPermission", "availableDealerCodes"]),
    dealerCodeFilter() {
      if (this.useUserFilter) {
        if (this.selectedOption === "All Branches") {
          return this.allAvailableDealerCodes;
        }
        return this.dealerFilter;
      } else {
        return this.allAvailableDealerCodes;
      }
    },
    userIsWebDeveloper() {
      return this.hasPermission("IsWebDeveloper");
    },
    userIsOrderAdmin() {
      return this.hasPermission("IsNGaugeOrderAdmin");
    },
    startDate() {
      let selectedMonth = this.monthFilter;
      if (!selectedMonth) {
        return null;
      }
      let start = moment.utc(selectedMonth, "MMMM YYYY");
      if (start.isValid()) {
        return start.startOf("month").format("MM/DD/YYYY hh:mm:ss A");
      }

      return null;
    },
    endDate() {
      let selectedMonth = this.monthFilter;
      if (!selectedMonth) {
        return null;
      }
      let end = moment.utc(selectedMonth, "MMMM YYYY");
      if (end.isValid()) {
        return end.endOf("month").format("MM/DD/YYYY hh:mm:ss A");
      }

      return null;
    },
  },
  created() {
    this.selectedOption = this.getContentItem("allBranches");
    this.message = this.getContentItem("youAreViewingAllBranches");
    this.partsStoreOrderColumns[0].label = this.getContentItem("orderNumber");
    this.partsStoreOrderColumns[1].label = this.getContentItem("customer");
    this.partsStoreOrderColumns[2].label = this.getContentItem("poNumber");
    this.partsStoreOrderColumns[3].label = this.getContentItem("dealerCodeHeader");
    this.partsStoreOrderColumns[4].label = this.getContentItem("date");
    this.partsStoreOrderColumns[5].label = this.getContentItem("status");
    this.partsStoreOrderColumns[6].label = this.getContentItem("total");

    var dcLength = this.availableDealerCodes.length;
    var all = this.availableDealerCodes;
    var returnCodes = [];
    let i;
    for (i = 0; i < dcLength; i++) {
      returnCodes.push(all[i].PrimaryDealerCode);
    }

    // start with all available dealer codes selected
    this.allAvailableDealerCodes = returnCodes;
    this.dealerFilter = returnCodes;
    this.OrderHeaderSearch();
  },
  methods: {
    handlePageChange(newPage) {
      this.pagination.currentPage = newPage;
      this.OrderHeaderSearch(); // Fetch new data for the selected page
    },

    OrderHeaderSearch: function () {
      this.isLoading = true;
      let sortDir = this.sortDesc === true ? 1 : 0;
      let orderBy = this.orderByField;

      // Map the sort by to the db field
      if (this.orderByField === "OrderNumber") {
        orderBy = "Id";
      }

      if (this.orderByField === "Total") {
        orderBy = "OrderTotalTargetCurrency";
      }

      let OrderHeaderSearchDto = {
        AccountCodes: this.dealerCodeFilter,
        OrderStatuses: this.orderStatusFilter,
        SearchQuery: this.searchQuery,
        // issysadmin removes the filter for Account Codes for performance, so only set true when all branches is selected
        IsSystemAdmin:
          (this.userIsWebDeveloper || this.user.DealerRange === "*") && this.selectedOption == "All Branches"
            ? true
            : false,
        OrderByDirection: sortDir,
        OrderByField: orderBy,
        StartDate: this.startDate,
        EndDate: this.endDate,
        Page: this.pagination.currentPage,
        PageSize: this.pagination.perPage,
      };

      return PartsStoreOrdersService.OrderHeaderSearch(
        this.user.PrimaryAccountId,
        this.user.UserId,
        OrderHeaderSearchDto
      )
        .then((partsStoreOrderIndexDto) => {
          this.pagination.totalRows = partsStoreOrderIndexDto.data.Total;
          this.pagination.currentPage = partsStoreOrderIndexDto.data.Page;
          this.psOrders = partsStoreOrderIndexDto.data.Items;
          return partsStoreOrderIndexDto.data.Items;
        })
        .catch((err) => {
          ErrorLogService.LogException(err);
          this.psOrders = [];
          return [];
        }).finally(() => {
          this.isLoading = false;
        });
    },
    SetUserDealerCode: function (selectedAvailableDealerCode) {
      this.useUserFilter = true;
      if (selectedAvailableDealerCode === "All Branches") {
        this.selectedOption = selectedAvailableDealerCode;
        this.dealerFilter = this.allAvailableDealerCodes;
      } else {
        this.selectedOption = selectedAvailableDealerCode.DisplayWithSubDealers;
        let dealerCodes = selectedAvailableDealerCode.DealerCodes;

        if (dealerCodes) {
          dealerCodes = dealerCodes.replace(/\s/g, "");
          dealerCodes = dealerCodes.split(",");
          this.dealerFilter = dealerCodes;
        }
      }
    },
    SetUserSelectedMonth: function (selectedMonth) {
      this.useUserFilter = true;
      this.monthFilter = selectedMonth;
    },
    // timeout function allows this method to be called every 350ms
    SearchBoxFilter: function () {
      //https://stackoverflow.com/q/37587800/vue-debounce-a-method
      clearTimeout(this.timer); // if this method is called before 350ms, than the refresh will be cancelled
      this.timer = setTimeout(
        function () {
          this.OrderHeaderSearch();
        }.bind(this),
        350
      );
    },

    FilterButtonClick: function () {
      this.useUserFilter = true;
      let selectedDealer = this.dealerFilter.length === 0 ? "All Branches" : this.selectedOption;
      let message = "You are viewing open orders for " + selectedDealer;
      if (this.monthFilter !== "" && this.monthFilter !== "Choose a Month") {
        message += " during the month of " + this.monthFilter;
      }
      this.message = message;
      this.OrderHeaderSearch();
    },
  },
};
</script>
